/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2021 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import React, { useEffect, useMemo, useState } from 'react'

import {
  getTypeOrganization,
  Organization,
  useCoordinator,
  useOrganizationsProject,
} from '@builddocs/data-management'
import { useDisclosure } from '@builddocs/system'
import {
  Typography,
  Button,
  Stack,
  DialogTitle,
  DialogContentText,
  DialogActions,
  DialogContent,
  Dialog,
  Paper,
} from '@mui/material'
import Draggable from 'react-draggable'

import TableForSelect from './TableForSelect'

type Props = {
  initialOrganization: Organization
  onSelect: (value: Organization | undefined) => void
  typeOrganization: string
  isSingleSelect?: boolean
}

function PaperComponent(props) {
  return (
    <Draggable
      cancel={'[class*="MuiDialogContent-root"]'}
      handle="#draggable-dialog-title"
    >
      <Paper {...props} />
    </Draggable>
  )
}

/**
 *Диалог выбора организации
 */

function OrganizationDialog(props: Props) {
  const {
    onSelect,
    initialOrganization,
    typeOrganization,
    isSingleSelect,
    ...rest
  } = props

  const { isOpen, onOpen, onClose } = useDisclosure()

  const { projectId, contractId } = useCoordinator() as {
    projectId: string
    contractId: string
  }
  const { organizations } = useOrganizationsProject(projectId, contractId) // получение списка организаций
  const [allOrganizations, setAllOrganizations] = useState<
    Record<string, Organization & { roleInContract: string }>
  >(
    Object.keys(organizations).reduce(
      (acc, role) => ({
        ...acc,
        [organizations[role].inn]: {
          ...organizations[role],
          roleInContract: getTypeOrganization(role),
        },
      }),
      {},
    ),
  )
  const [selectedInn, setSelectedInn] = useState<string>(
    initialOrganization?.inn ?? '',
  ) // орг-и переданные в пропсах изначально изначально являются выбранными

  useEffect(
    () =>
      setAllOrganizations(
        Object.keys(organizations).reduce(
          (acc, role) => ({
            ...acc,
            [organizations[role].inn]: {
              ...organizations[role],
              roleInContract: getTypeOrganization(role),
            },
          }),
          {},
        ),
      ),
    [organizations],
  ) // обновления состояния в случае изменения первоначальнонго списка орг-й
  //проверка на то что орг-я выбрана
  const hasValue = useMemo(() => selectedInn, [selectedInn])

  const handleSelect = (inn: string | string[]) => {
    if (inn === selectedInn) {
      setSelectedInn('')
    } else {
      Array.isArray(inn) ? setSelectedInn(inn[0]) : setSelectedInn(inn) //добавление орг-и в выбранные
    }
  }

  const handleClose = () => {
    setSelectedInn(initialOrganization?.inn) //возвращение первоначального состояния выбранных орг-й
    onClose()
  }

  const handleSave = () => {
    hasValue
      ? onSelect(
          Object.values(allOrganizations).find(org => org.inn === selectedInn),
        )
      : onSelect(undefined) //сохранение выбора
    onClose()
  }

  const handleOpen = (event: React.MouseEvent) => {
    //открытие диалогового окна выбора орг-и
    event.preventDefault()
    onOpen()
  }

  return (
    <>
      <Stack alignItems="flex-start" {...rest}>
        {initialOrganization && (
          <Typography color="text.secondary" variant="subtitle2">
            {`${initialOrganization.name}`}
          </Typography>
        )}
        <Button onClick={handleOpen}>
          {initialOrganization
            ? 'Изменить организацию'
            : 'Выберите организацию'}
        </Button>
      </Stack>
      <Dialog
        PaperComponent={PaperComponent}
        aria-labelledby="draggable-dialog-title"
        id="organization-dialog-modal"
        maxWidth="md"
        open={isOpen}
        onClose={handleClose}
      >
        <DialogTitle id="draggable-dialog-title" style={{ cursor: 'move' }}>
          Выберите организацию
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Список организаций участвующих в проекте
          </DialogContentText>
          {allOrganizations && (
            <TableForSelect
              isSingleSelect={isSingleSelect}
              items={allOrganizations}
              selected={[selectedInn]}
              tableType="organizations"
              onSelect={handleSelect}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            Отмена
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default OrganizationDialog
